import { useState, useEffect } from 'react';
import adminApi from '@/lib/adminApi';
import { toast } from 'sonner';
import { 
  Plus, Trash2, Edit2, Save, X, MessageCircle, 
  Image, Loader2, Package, ChevronDown, ChevronUp,
  GripVertical, Eye, EyeOff
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import ImageUploader from './ImageUploader';

export default function StickersTab() {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPack, setExpandedPack] = useState(null);
  const [editingPack, setEditingPack] = useState(null);
  const [savingPack, setSavingPack] = useState(null);
  const [addingSticker, setAddingSticker] = useState(null);
  
  // New pack form
  const [newPackForm, setNewPackForm] = useState({
    identifier: '',
    name: '',
    publisher: 'Darkar Company Studios',
    tray_image_url: '',
    animated: false
  });
  
  // New sticker form
  const [newStickerForm, setNewStickerForm] = useState({
    image_url: '',
    emojis: ''
  });

  useEffect(() => {
    fetchPacks();
  }, []);

  const fetchPacks = async () => {
    try {
      const res = await adminApi.get('/stickers/packs/all');
      setPacks(res.data);
    } catch (error) {
      console.error('Error fetching sticker packs:', error);
      toast.error('Error al cargar los paquetes de stickers');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePack = async (e) => {
    e.preventDefault();
    if (!newPackForm.identifier || !newPackForm.name) {
      toast.error('Identificador y nombre son requeridos');
      return;
    }

    try {
      const res = await adminApi.post('/stickers/packs', newPackForm);
      setPacks([...packs, res.data]);
      setNewPackForm({
        identifier: '',
        name: '',
        publisher: 'Darkar Company Studios',
        tray_image_url: '',
        animated: false
      });
      toast.success('Paquete creado');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al crear paquete';
      toast.error(msg);
    }
  };

  const handleUpdatePack = async (packId) => {
    const pack = packs.find(p => p.id === packId);
    if (!pack) return;
    
    setSavingPack(packId);
    try {
      await adminApi.put(`/stickers/packs/${packId}`, {
        identifier: pack.identifier,
        name: pack.name,
        publisher: pack.publisher,
        tray_image_url: pack.tray_image_url,
        animated: pack.animated,
        active: pack.active,
        order: pack.order
      });
      toast.success('Paquete actualizado');
    } catch (error) {
      toast.error('Error al actualizar paquete');
    } finally {
      setSavingPack(null);
    }
  };

  const handleDeletePack = async (packId) => {
    if (!window.confirm('¿Eliminar este paquete y todos sus stickers?')) return;
    
    try {
      await adminApi.delete(`/stickers/packs/${packId}`);
      setPacks(packs.filter(p => p.id !== packId));
      toast.success('Paquete eliminado');
    } catch (error) {
      toast.error('Error al eliminar paquete');
    }
  };

  const handleLocalPackUpdate = (packId, field, value) => {
    setPacks(packs.map(p => 
      p.id === packId ? { ...p, [field]: value } : p
    ));
  };

  const handleAddSticker = async (packId) => {
    if (!newStickerForm.image_url) {
      toast.error('La imagen es requerida');
      return;
    }

    setAddingSticker(packId);
    try {
      const emojis = newStickerForm.emojis
        .split(',')
        .map(e => e.trim())
        .filter(e => e);
      
      const res = await adminApi.post(`/stickers/packs/${packId}/stickers`, {
        image_url: newStickerForm.image_url,
        emojis: emojis
      });
      
      setPacks(packs.map(p => p.id === packId ? res.data : p));
      setNewStickerForm({ image_url: '', emojis: '' });
      toast.success('Sticker agregado');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al agregar sticker';
      toast.error(msg);
    } finally {
      setAddingSticker(null);
    }
  };

  const handleDeleteSticker = async (packId, stickerId) => {
    try {
      await adminApi.delete(`/stickers/packs/${packId}/stickers/${stickerId}`);
      setPacks(packs.map(p => {
        if (p.id === packId) {
          return {
            ...p,
            stickers: p.stickers.filter(s => s.id !== stickerId)
          };
        }
        return p;
      }));
      toast.success('Sticker eliminado');
    } catch (error) {
      toast.error('Error al eliminar sticker');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <MessageCircle className="text-green-500" size={24} />
        <h2 className="text-xl font-bold text-black">Stickers de WhatsApp</h2>
      </div>

      {/* Info */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
        <p className="text-sm text-green-700">
          <strong>Conversión automática:</strong> Sube imágenes PNG o JPG de cualquier tamaño. 
          El sistema las convierte automáticamente a WebP optimizado (512x512 para stickers, 96x96 para el ícono del paquete).
          Cada paquete debe tener entre 3 y 30 stickers.
        </p>
      </div>

      {/* Create New Pack Form */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
        <h3 className="text-lg font-bold text-black mb-4 flex items-center gap-2">
          <Plus size={20} />
          Crear Nuevo Paquete
        </h3>
        <form onSubmit={handleCreatePack} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1 font-medium">
                Identificador único *
              </label>
              <Input
                value={newPackForm.identifier}
                onChange={(e) => setNewPackForm({ ...newPackForm, identifier: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                placeholder="darkar_pack_1"
                className="bg-gray-50 border-gray-200"
              />
              <p className="text-xs text-gray-400 mt-1">Solo letras minúsculas, números y guiones bajos</p>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1 font-medium">
                Nombre del paquete *
              </label>
              <Input
                value={newPackForm.name}
                onChange={(e) => setNewPackForm({ ...newPackForm, name: e.target.value })}
                placeholder="Darkar Stickers Vol. 1"
                className="bg-gray-50 border-gray-200"
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1 font-medium">
                Publicador
              </label>
              <Input
                value={newPackForm.publisher}
                onChange={(e) => setNewPackForm({ ...newPackForm, publisher: e.target.value })}
                className="bg-gray-50 border-gray-200"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1 font-medium">
                Ícono del paquete (96x96)
              </label>
              <ImageUploader
                value={newPackForm.tray_image_url}
                onChange={(url) => setNewPackForm({ ...newPackForm, tray_image_url: url })}
                category="stickers"
                stickerType="tray"
                placeholder="Sube PNG/JPG (se convierte a WebP 96x96)"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={newPackForm.animated}
              onCheckedChange={(checked) => setNewPackForm({ ...newPackForm, animated: checked })}
            />
            <span className="text-sm text-gray-600">Stickers animados</span>
          </div>
          <Button type="submit" className="bg-green-500 hover:bg-green-600 text-white">
            <Plus size={16} className="mr-2" />
            Crear Paquete
          </Button>
        </form>
      </div>

      {/* Packs List */}
      <div className="space-y-4">
        {packs.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
            <Package className="mx-auto text-gray-300 mb-4" size={48} />
            <p className="text-gray-500">No hay paquetes de stickers</p>
            <p className="text-gray-400 text-sm mt-1">Crea uno para comenzar</p>
          </div>
        ) : (
          packs.map((pack) => (
            <div 
              key={pack.id}
              className={`bg-white border rounded-2xl overflow-hidden transition-all ${
                pack.active ? 'border-gray-200' : 'border-red-200 bg-red-50/30'
              }`}
            >
              {/* Pack Header */}
              <div className="p-4 flex items-center gap-4">
                {/* Tray Icon */}
                <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {pack.tray_image_url ? (
                    <img src={pack.tray_image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <MessageCircle className="text-gray-400" size={24} />
                  )}
                </div>

                {/* Pack Info */}
                <div className="flex-1 min-w-0">
                  <Input
                    value={pack.name}
                    onChange={(e) => handleLocalPackUpdate(pack.id, 'name', e.target.value)}
                    className="font-bold text-black bg-transparent border-0 p-0 h-auto text-lg focus:ring-0"
                  />
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">
                      {pack.identifier}
                    </span>
                    <span>•</span>
                    <span>{pack.stickers?.length || 0}/30 stickers</span>
                    {pack.animated && (
                      <>
                        <span>•</span>
                        <span className="text-purple-600">Animados</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 mr-4">
                    {pack.active ? (
                      <Eye className="text-green-500" size={16} />
                    ) : (
                      <EyeOff className="text-red-500" size={16} />
                    )}
                    <Switch
                      checked={pack.active}
                      onCheckedChange={(checked) => handleLocalPackUpdate(pack.id, 'active', checked)}
                    />
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleUpdatePack(pack.id)}
                    disabled={savingPack === pack.id}
                    className="text-green-600 border-green-200 hover:bg-green-50"
                  >
                    {savingPack === pack.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Save size={14} />
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setExpandedPack(expandedPack === pack.id ? null : pack.id)}
                  >
                    {expandedPack === pack.id ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDeletePack(pack.id)}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>

              {/* Expanded Content */}
              {expandedPack === pack.id && (
                <div className="border-t border-gray-100 p-4 bg-gray-50/50">
                  {/* Add Sticker Form */}
                  <div className="bg-white rounded-xl p-4 mb-4 border border-gray-200">
                    <h4 className="font-semibold text-black mb-3 flex items-center gap-2">
                      <Plus size={16} />
                      Agregar Sticker
                    </h4>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm text-gray-600 mb-1">Imagen (se convierte a 512x512 WebP)</label>
                        <ImageUploader
                          value={newStickerForm.image_url}
                          onChange={(url) => setNewStickerForm({ ...newStickerForm, image_url: url })}
                          category="stickers"
                          stickerType="sticker"
                          placeholder="Sube PNG/JPG (se convierte automáticamente)"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Emojis (separados por coma)</label>
                        <Input
                          value={newStickerForm.emojis}
                          onChange={(e) => setNewStickerForm({ ...newStickerForm, emojis: e.target.value })}
                          placeholder="😀, 😎, 🎉"
                          className="bg-gray-50"
                        />
                      </div>
                    </div>
                    <Button
                      onClick={() => handleAddSticker(pack.id)}
                      disabled={addingSticker === pack.id || !newStickerForm.image_url}
                      className="mt-3 bg-green-500 hover:bg-green-600 text-white"
                      size="sm"
                    >
                      {addingSticker === pack.id ? (
                        <Loader2 size={14} className="animate-spin mr-2" />
                      ) : (
                        <Plus size={14} className="mr-2" />
                      )}
                      Agregar Sticker
                    </Button>
                  </div>

                  {/* Stickers Grid */}
                  {pack.stickers?.length > 0 ? (
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
                      {pack.stickers.map((sticker, index) => (
                        <div 
                          key={sticker.id}
                          className="relative group aspect-square bg-white rounded-lg border border-gray-200 overflow-hidden"
                        >
                          <img 
                            src={sticker.image_url} 
                            alt={`Sticker ${index + 1}`}
                            className="w-full h-full object-contain p-1"
                          />
                          <button
                            onClick={() => handleDeleteSticker(pack.id, sticker.id)}
                            className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                          >
                            <X size={12} />
                          </button>
                          {sticker.emojis?.length > 0 && (
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs text-center py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              {sticker.emojis.join(' ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-gray-400 py-4">
                      No hay stickers en este paquete. Agrega al menos 3 para publicar.
                    </p>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
